import React, {FormEvent, useState} from "react";
import {Tab} from "@headlessui/react";
import TabButton from "../../../TabButton";
import {FormikContextType, useFormikContext} from "formik";
import {UrlField} from "../Field";
import Field from "../../Field";
import {useQuery} from "react-query";
import ngage from "../../../../services/ngage";
import {Option, SingleCombobox} from "../Control";
import {MessageAction} from "../../../../types/Message";
import MessageActionType from "../../../../types/MessageActionType";
import {useOperator} from "../../../../context/OperatorContext";

// Types ----

export type MessageActionFieldSetProps = {
  name: string
  label?: string
  required?: boolean
}

type TabId = 'app' | 'web'

type TabDefinition = {
  id: TabId
  name: string
}

// Helpers ----

const tabs: TabDefinition[] = [
  {id: 'app', name: 'App Content'},
  {id: 'web', name: 'Web Page'},
];

function tabIdFromAction(action?: MessageAction) {
  return action?.type === 'url' ? 'web' : 'app' as TabId;
}

function getFormValue(form: FormikContextType<any>, name: string) {
  return form.values[name] as MessageAction | undefined;
}

function actionIdFromAction(action?: MessageAction) {
  const value = Object.values(action?.data ?? {})[0];

  if(!value) return null;

  return action?.type === MessageActionType.Url? String(value) : Number(value);
}

async function getAchievementOptions(searchTerm: string): Promise<Option[]> {
  const {data: achievements} = await ngage.achievements.all();

  const filteredAchievements = searchTerm ?
    achievements.filter(achievement => achievement.name.toLowerCase().includes(searchTerm.toLowerCase())) :
    achievements;

  return filteredAchievements.map(achievement => ({id: achievement.id, name: achievement.name}));
}

async function getArticleOptions(searchTerm: string): Promise<Option[]> {
  const {data: articles} = await ngage.articles.filtered(searchTerm ? {search: searchTerm} : {});

  return articles.map(article => ({id: article.id, name: article.title}));
}

async function getCollectionOptions(searchTerm: string): Promise<Option[]> {
  const {data: collections} = await ngage.collections.all();

  const filteredCollections = searchTerm ?
    collections.filter(collection => collection.name.toLowerCase().includes(searchTerm.toLowerCase())) :
    collections;

  return filteredCollections.map(collection => ({id: collection.id, name: collection.name}))
}

async function getEventOptions(searchTerm: string): Promise<Option[]> {
  const {data: events} = await ngage.events.filtered(searchTerm ? {search: searchTerm} : {});

  return events.map(event => ({id: event.id, name: event.name, description: `${event.type.name} (${event.location.type === 'place' ? event.location.name : event.location.site.name})`}));
}

async function getExhibitOptions(searchTerm: string): Promise<Option[]> {
  const searchFilter = searchTerm ? {search: searchTerm } : {};
  const {data: exhibits} = await ngage.exhibits.filtered({...searchFilter, published: true});

  return exhibits.map(exhibit => ({id: exhibit.id, name: exhibit.name, description: `${exhibit.type.name} (${exhibit.zone.site.name})`}));
}

async function getFacilityOptions(searchTerm: string): Promise<Option[]> {
  const searchFilter = searchTerm ? {search: searchTerm } : {};
  const {data: facilities} = await ngage.facilities.filtered({...searchFilter, published: true});

  return facilities.map(facility => ({id: facility.id, name: facility.name, description: `${facility.type.name} (${facility.zone.site.name})`}));
}

async function getQuizOptions(searchTerm: string): Promise<Option[]> {
  const {data: quizzes} = await ngage.quizzes.filtered(searchTerm ? {search: searchTerm} : {});

  return quizzes.map(quiz => ({id: quiz.id, name: quiz.title}));
}

async function getTrailOptions(searchTerm: string): Promise<Option[]> {
  const {data: trails} = await ngage.trails.filtered(searchTerm ? {search: searchTerm} : {});

  return trails.map(trail => ({id: trail.id, name: trail.name, description: trail.site?.name}));
}

// Component ----

export default function MessageActionFieldSet({name, label, required = false}: MessageActionFieldSetProps) {
  const {termFor} = useOperator();
  const form = useFormikContext();

  // Check the form for the initial field value
  const formValue = getFormValue(form, name);

  // Keep track of the tab chosen
  const [selectedTabId, setSelectedTabId] = useState<TabId>(tabIdFromAction(formValue));
  const selectedTabIndex = tabs.findIndex(tab => tab.id === selectedTabId);

  // Keep track of the selected content type and ID
  const [selectedActionType, setSelectedActionType] = useState<MessageActionType>(formValue?.type ?? MessageActionType.Overview);
  const [selectedActionId, setSelectedActionId] = useState<number | string | null>(actionIdFromAction(formValue));

  // Handle tab selection
  function handleTabSelect(index: number) {
    const tabId = tabs[index]?.id ?? 'app' as TabId;
    const actionType = tabId === 'web'? MessageActionType.Url : MessageActionType.Overview;

    void form.setFieldValue(`${name}.type`, actionType);
    void form.setFieldValue(`${name}.data`, {});

    setSelectedTabId(tabId);
    setSearchTerm('');
    setSelectedActionType(actionType);
    setSelectedActionId(null);
  }

  // Handle action type selection
  function handleSelectActionType({currentTarget}: FormEvent<HTMLSelectElement>) {
    void form.setFieldValue(`${name}.type`, currentTarget.value);
    void form.setFieldValue(`${name}.data`, {});

    setSearchTerm('');
    setSelectedActionType(currentTarget.value as MessageActionType);
    setSelectedActionId(null);
  }

  // Handle action selection
  function handleSelectActionId(option: Option | null) {
    let key: string | null = null;
    let actionId: number | string | null = null;

    if (selectedActionType.includes('-')) {
      key = selectedActionType.split('-')[0] + 'Id';
      actionId = Number(option?.id);
    }

    if(key) void form.setFieldValue(`${name}.data.${key}`, actionId);

    setSearchTerm('');
    setSelectedActionId(actionId);
  }

  // Keep track of the current search term
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Find achievement options
  const {data: achievements, isLoading: isLoadingAchievements} = useQuery({
    queryKey: ['achievements', searchTerm],
    queryFn: () => getAchievementOptions(searchTerm),
    enabled: selectedActionType.startsWith('achievement-'),
  })

  // Find articles options
  const {data: articles, isLoading: isLoadingArticles} = useQuery({
    queryKey: ['articles', searchTerm],
    queryFn: () => getArticleOptions(searchTerm),
    enabled: selectedActionType.startsWith('article-'),
  })

  // Find collection options
  const {data: collections, isLoading: isLoadingCollections} = useQuery({
    queryKey: ['collections', searchTerm],
    queryFn: () => getCollectionOptions(searchTerm),
    enabled: selectedActionType.startsWith('collection-'),
  })

  // Find event options
  const {data: events, isLoading: isLoadingEvents} = useQuery({
    queryKey: ['events', searchTerm],
    queryFn: () => getEventOptions(searchTerm),
    enabled: selectedActionType.startsWith('event-'),
  })

  // Find exhibit options
  const {data: exhibits, isLoading: isLoadingExhibits} = useQuery({
    queryKey: ['exhibits', searchTerm],
    queryFn: () => getExhibitOptions(searchTerm),
    enabled: selectedActionType.startsWith('exhibit-'),
  })

  // Find facility options
  const {data: facilities, isLoading: isLoadingFacilities} = useQuery({
    queryKey: ['facilities', searchTerm],
    queryFn: () => getFacilityOptions(searchTerm),
    enabled: selectedActionType.startsWith('facility-'),
  })

  // Find quiz options
  const {data: quizzes, isLoading: isLoadingQuizzes} = useQuery({
    queryKey: ['quizzes', searchTerm],
    queryFn: () => getQuizOptions(searchTerm),
    enabled: selectedActionType.startsWith('quiz-'),
  })

  // Find trail options
  const {data: trails, isLoading: isLoadingTrails} = useQuery({
    queryKey: ['trails', searchTerm],
    queryFn: () => getTrailOptions(searchTerm),
    enabled: selectedActionType.startsWith('trail-'),
  })

  // Render component
  return (
    <fieldset>
      {!!label &&
        <legend className="block text-sm font-medium leading-6 text-gray-900">
          {label}
        </legend>
      }

      {/* Action Type */}
      <div className="mb-2 mt-2">
        <Tab.Group selectedIndex={selectedTabIndex} onChange={handleTabSelect}>
          <Tab.List>
            {tabs.map((tab, index) => (
              <TabButton position={index === 0 ? TabButton.positions.FIRST : TabButton.positions.LAST} key={index}>
                {tab.name}
              </TabButton>
            ))}
          </Tab.List>
        </Tab.Group>
      </div>

      {/* Action Data */}
      <div>
        {selectedTabId === 'app' &&
          <div className="sm:grid sm:grid-cols-2 sm:gap-2">
            <Field
              as='select'
              id={`${name}.type`}
              name={`${name}.type`}
              value={selectedActionType}
              onChange={handleSelectActionType}
            >
              <option value='' disabled>Select action type...</option>

              <optgroup label='Account'>
                <option value='user'>User Account Details</option>
              </optgroup>

              <optgroup label='Achievements'>
                <option value='achievements'>All Achievements</option>
                <option value='achievement-information'>Achievement Information</option>
              </optgroup>

              <optgroup label='Activities'>
                <option value='activities'>All Activities</option>
              </optgroup>

              <optgroup label='Collections'>
                <option value='collections'>All Collections</option>
                <option value='collection-information'>Collection Information</option>
              </optgroup>

              <optgroup label='Events'>
                <option value='events'>All Events</option>
                <option value='event-information'>Event Information</option>
              </optgroup>

              <optgroup label='Exhibits'>
                <option value='exhibits'>All Exhibits</option>
                <option value='exhibit-information'>Exhibit Information</option>
                <option value='exhibit-notes'>Exhibit Notes</option>
                <option value='exhibit-quizzes'>Exhibit Quizzes</option>
                <option value='exhibit-sponsorships'>Exhibit Sponsorships</option>
              </optgroup>

              <optgroup label='Facilities'>
                <option value='facilities'>All Facilities</option>
                <option value='facility-information'>Facility Information</option>
              </optgroup>

              <optgroup label='Frames'>
                <option value='frames'>Capture Your Day</option>
              </optgroup>

              <optgroup label='News'>
                <option value='articles'>All News</option>
                <option value='article-information'>Article</option>
              </optgroup>

              <optgroup label='Overview'>
                <option value='overview'>Overview</option>
              </optgroup>

              <optgroup label='Quizzes'>
                <option value='quiz-information'>Specific Quiz</option>
              </optgroup>

              <optgroup label='Sites'>
                <option value='sites'>Site Selector</option>
              </optgroup>

              <optgroup label='Trails'>
                <option value='trails'>All Trails</option>
                <option value='trail-information'>Trail Information</option>
              </optgroup>

              <optgroup label='Visits'>
                <option value='visits'>Visit Planner</option>
              </optgroup>

              <optgroup label="Wallet">
                <option value='tickets'>{termFor('Tickets')}</option>
                <option value='memberships'>{termFor('Memberships')}</option>
                <option value='vouchers'>{termFor('Vouchers')}</option>
              </optgroup>
            </Field>

            {selectedActionType.includes('-') &&
              <div>
                {selectedActionType.startsWith('achievement-') &&
                  <SingleCombobox
                    isLoading={isLoadingAchievements}
                    options={achievements ?? []}
                    selected={achievements?.find(option => option.id === selectedActionId)}
                    onSearch={term => setSearchTerm(term ?? '')}
                    onChange={handleSelectActionId}
                  />
                }

                {selectedActionType.startsWith('collection-') &&
                  <SingleCombobox
                    isLoading={isLoadingCollections}
                    options={collections ?? []}
                    selected={collections?.find(option => option.id === selectedActionId)}
                    onSearch={term => setSearchTerm(term ?? '')}
                    onChange={handleSelectActionId}
                  />
                }

                {selectedActionType.startsWith('event-') &&
                  <SingleCombobox
                    isLoading={isLoadingEvents}
                    options={events ?? []}
                    selected={events?.find(option => option.id === selectedActionId)}
                    onSearch={term => setSearchTerm(term ?? '')}
                    onChange={handleSelectActionId}
                  />
                }

                {selectedActionType.startsWith('exhibit-') &&
                  <SingleCombobox
                    isLoading={isLoadingExhibits}
                    options={exhibits ?? []}
                    selected={exhibits?.find(option => option.id === selectedActionId)}
                    onSearch={term => setSearchTerm(term ?? '')}
                    onChange={handleSelectActionId}
                  />
                }

                {selectedActionType.startsWith('facility-') &&
                  <SingleCombobox
                    isLoading={isLoadingFacilities}
                    options={facilities ?? []}
                    selected={facilities?.find(option => option.id === selectedActionId)}
                    onSearch={term => setSearchTerm(term ?? '')}
                    onChange={handleSelectActionId}
                  />
                }

                {selectedActionType.startsWith('article-') &&
                  <SingleCombobox
                    isLoading={isLoadingArticles}
                    options={articles ?? []}
                    selected={articles?.find(option => option.id === selectedActionId)}
                    onSearch={term => setSearchTerm(term ?? '')}
                    onChange={handleSelectActionId}
                  />
                }

                {selectedActionType.startsWith('quiz-') &&
                  <SingleCombobox
                    isLoading={isLoadingQuizzes}
                    options={quizzes ?? []}
                    selected={quizzes?.find(option => option.id === selectedActionId)}
                    onSearch={term => setSearchTerm(term ?? '')}
                    onChange={handleSelectActionId}
                  />
                }

                {selectedActionType.startsWith('trail-') &&
                  <SingleCombobox
                    isLoading={isLoadingTrails}
                    options={trails ?? []}
                    selected={trails?.find(option => option.id === selectedActionId)}
                    onSearch={term => setSearchTerm(term ?? '')}
                    onChange={handleSelectActionId}
                  />
                }
              </div>
            }
          </div>
        }

        {selectedTabId === 'web' &&
          <UrlField name={`${name}.data.url`} schemes={["https"]} />
        }
      </div>

    </fieldset>
  );
}